import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { editStoreInfo } from "../../hooks/apiHandler"; 
import { useSnackbar } from "../../hooks/SnackbarProvider";
import { useAuth } from "../../hooks/useAuth";
import { ALL_COUNTRIES } from "../../helperFunc/countylist";

// --- Utility Functions & Constants ---
const DAY_MAP = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DEFAULT_TIME_OBJ = { hours: 9, minutes: 0 };
const DEFAULT_CLOSE_OBJ = { hours: 21, minutes: 0 }; 

const denormalizeScheduleFromAPI = (scheduleArray) => {
    const apiMap = scheduleArray.reduce((acc, item) => {
        const [openH, openM] = item.openTime ? item.openTime.split(':').map(Number) : [DEFAULT_TIME_OBJ.hours, DEFAULT_TIME_OBJ.minutes];
        const [closeH, closeM] = item.closeTime ? item.closeTime.split(':').map(Number) : [DEFAULT_CLOSE_OBJ.hours, DEFAULT_CLOSE_OBJ.minutes];

        acc[item.dayOfWeek] = {
            day: DAY_MAP[item.dayOfWeek],
            storeOpen: item.storeOpen,
            openTime: { hours: openH, minutes: openM },
            closeTime: { hours: closeH, minutes: closeM },
        };
        return acc;
    }, {});

    return DAY_MAP.map((dayName, index) => apiMap[index] || {
        day: dayName,
        storeOpen: false,
        openTime: DEFAULT_TIME_OBJ,
        closeTime: DEFAULT_CLOSE_OBJ,
    });
};

const normalizeScheduleForAPI = (scheduleArray) => {
    return scheduleArray.map((times, index) => ({
        dayOfWeek: index,
        storeOpen: times.storeOpen,
        openTime: `${times.openTime.hours.toString().padStart(2, '0')}:${times.openTime.minutes.toString().padStart(2, '0')}`,
        closeTime: `${times.closeTime.hours.toString().padStart(2, '0')}:${times.closeTime.minutes.toString().padStart(2, '0')}`,
    }));
};

const getRegionFullName = (countryCode, regionCode) => {
    if (!countryCode || !regionCode) return "";
    const country = ALL_COUNTRIES.find(c => c.code === countryCode);
    if (!country) return regionCode;
    const region = country.regions.find(r => r.code === regionCode);
    return region?.name || regionCode;
};

// --- Main Component ---

export const GeneralSetting = ({ data, onUpdate }) => {
    const theme = useTheme();
    const isMdOrLg = useMediaQuery(theme.breakpoints.up('md'));
    const [editMode, setEditMode] = useState(false);

    const storeid = JSON.parse(window.localStorage.getItem('ds'));
    const { Logout } = useAuth();
    const showSnackbar = useSnackbar();

    const [localStoreInfo, setLocalStoreInfo] = useState({
        storeID: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postCode: '',
        storeSchedule: denormalizeScheduleFromAPI([])
    });

    useEffect(() => {
        if (data) {
            setLocalStoreInfo({
                ...data,
                phone: data.phone?.toString() || '',
                storeSchedule: denormalizeScheduleFromAPI(data.storeSchedule || [])
            });
        }
    }, [data]);

    const updateScheduleItem = (index, updates) => {
        setLocalStoreInfo(prevInfo => {
            const newSchedule = [...prevInfo.storeSchedule];
            newSchedule[index] = { ...newSchedule[index], ...updates };
            return { ...prevInfo, storeSchedule: newSchedule };
        });
    };

    const handleSwitchChange = (event, index) => {
        updateScheduleItem(index, { storeOpen: event.target.checked });
    };

    const handleTimeChange = (event, index, timeType) => {
        const [hours, minutes] = event.target.value.split(':').map(Number);
        updateScheduleItem(index, {
            [timeType]: { hours, minutes }
        });
    };

    const handleAddressInputChange = (e, field) => {
        setLocalStoreInfo(prev => ({ ...prev, [field]: e.target.value }));
    };

    const timeDisplay = (time) => {
        if (!time || time.hours === undefined || time.minutes === undefined) return "00:00";
        const { hours, minutes } = time;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '1000px',boxSizing: 'border-box' }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={8}>
                    <Typography variant="h6" component="h4">Store Info</Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    {editMode ? (
                        <>
                            <Button
                                color="inherit"
                                onClick={() => {
                                    setEditMode(false);
                                    if (data) {
                                        setLocalStoreInfo({
                                            ...data,
                                            phone: data.phone?.toString() || '',
                                            storeSchedule: denormalizeScheduleFromAPI(data.storeSchedule || [])
                                        });
                                    }
                                }}>Cancel</Button>
                            &nbsp;
                            <Button variant="contained"
                                onClick={async () => {
                                    setEditMode(false);
                                    const apiSchedule = normalizeScheduleForAPI(localStoreInfo.storeSchedule);
                                    const updates = { ...localStoreInfo, storeSchedule: apiSchedule };
                                    try {
                                        var resp = await editStoreInfo({ storeID: storeid, updates: updates }, Logout);
                                        if (resp.code === 0) {
                                            showSnackbar('Store Information updated successful!', 'success');
                                            if (onUpdate) onUpdate(resp.data);
                                        } else {
                                            showSnackbar('Error: ' + resp.msg, 'error');
                                        }
                                    } catch (error) {
                                        // console.log('Update error:', error);
                                        showSnackbar(error.msg || "Update Failed", 'error');
                                    }
                                }}>Save</Button>
                        </>
                    ) : (
                        <Button variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>
                    )}
                </Grid>
            </Grid>
            
            <hr style={{ opacity: 0.5, marginBottom: '20px' }} />

            {/* --- Store Info Fields --- */}
            {/* Using spacing={2} to add gaps between stacked items on mobile */}
            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={3}><Typography>Store Name:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        value={localStoreInfo.name}
                        onChange={(e) => handleAddressInputChange(e, 'name')}
                        disabled={!editMode}
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>Store Phone:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        type='number' placeholder="(000)-000-0000"
                        value={localStoreInfo.phone}
                        onChange={(e) => handleAddressInputChange(e, 'phone')}
                        disabled={!editMode}
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>Store Email:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        type='text' placeholder="xyz@store.com"
                        value={localStoreInfo.email}
                        onChange={(e) => handleAddressInputChange(e, 'email')}
                        disabled={!editMode}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 2 }}>
                <Typography variant="h6" component="h4">Store Address</Typography>
            </Box>
            <hr style={{ opacity: 0.5, marginBottom: '20px' }} />
            
            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={3}><Typography>Address:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        placeholder="123 Center Street NE"
                        value={localStoreInfo.address}
                        onChange={(e) => handleAddressInputChange(e, 'address')}
                        disabled={!editMode}
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>City:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        placeholder="Calgary"
                        value={localStoreInfo.city}
                        onChange={(e) => handleAddressInputChange(e, 'city')}
                        disabled={!editMode}
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>Postal Code:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        placeholder="1A1 1B1"
                        value={localStoreInfo.postCode}
                        onChange={(e) => handleAddressInputChange(e, 'postCode')}
                        disabled={!editMode}
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>State / Province:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        placeholder="Alberta"
                        value={getRegionFullName(localStoreInfo.country, localStoreInfo.state)}
                        disabled={true} // Usually region depends on country dropdown logic not shown here
                    />
                </Grid>

                <Grid item xs={12} md={3}><Typography>Country:</Typography></Grid>
                <Grid item xs={12} md={9}>
                    <TextField fullWidth variant="filled" size="small"
                        placeholder="Canada"
                        value={ALL_COUNTRIES.find((st) => localStoreInfo.country === st.code)?.name}
                        disabled={true}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 2 }}>
                <Typography variant="h6" component="h4">Store Timing</Typography>
            </Box>
            <hr style={{ opacity: 0.5, marginBottom: '20px' }} />

            <Grid container spacing={2}>
                {localStoreInfo.storeSchedule.map((times, index) => (
                    <React.Fragment key={times.day}>
                        {/* RESPONSIVE LOGIC:
                           xs={6} sm={2}: On mobile, Day and Switch take half width each (one row). 
                                          On Tablet+, they take smaller slices.
                           xs={6} sm={3}: Time inputs take half width each on mobile (second row).
                        */}
                        
                        {/* 1. Day Name */}
                        <Grid item xs={6} sm={3} md={2} display="flex" alignItems="center">
                            <Typography sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{times.day}</Typography>
                        </Grid>

                        {/* 2. Toggle Switch */}
                        <Grid item xs={6} sm={3} md={2} display="flex" alignItems="center" justifyContent={{xs: 'flex-end', sm: 'flex-start'}}>
                             <Switch
                                checked={times.storeOpen}
                                onChange={(e) => handleSwitchChange(e, index)}
                                disabled={!editMode}
                            />
                        </Grid>

                        {/* 3. Open Time Input */}
                        <Grid item xs={6} sm={3} md={4}>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="time"
                                label={isMdOrLg ? "" : "Open"} // Label helps on mobile
                                value={timeDisplay(times.openTime)}
                                onChange={(e) => handleTimeChange(e, index, 'openTime')}
                                size="small"
                                disabled={!editMode || !times.storeOpen}
                            />
                        </Grid>

                        {/* 4. Close Time Input */}
                        <Grid item xs={6} sm={3} md={4}>
                            <TextField
                                fullWidth
                                variant="filled"
                                type="time"
                                label={isMdOrLg ? "" : "Close"}
                                value={timeDisplay(times.closeTime)}
                                onChange={(e) => handleTimeChange(e, index, 'closeTime')}
                                size="small"
                                disabled={!editMode || !times.storeOpen}
                            />
                        </Grid>
                        
                        {/* Divider for mobile clarity only */}
                        <Grid item xs={12} sx={{ display: { sm: 'none' }, mb: 2 }}>
                             <hr style={{ opacity: 0.2 }} />
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>

        </Box >
    )
}